import React, { useMemo, useRef, useState } from 'react';
import { Drawer } from "antd";
import { ReactComponent as ArrowLeftIcon } from '../../../assets/images/arrow-left.svg';
import { Pen2 } from '@solar-icons/react';
import { ReactComponent as RightArrowIcon } from "../../../assets/images/arrow-right-grey.svg";
import { ReactComponent as DirectionIcon } from "../../../assets/images/direction-hr.svg";

import {
  DetailsWrapper,
  BackIconWrapper, Wrapper, EditIconWrapper, MobileDrawerContent, SearchSummaryPill, FilterIconButton
} from './styles';
import { UserOutlined, MenuOutlined } from '@ant-design/icons';
import { useAuthStore } from "../../../store/auth/authStore";
import { StyledParagraph, StyledTitle } from "../../CustomAntdComponents/Typography";
import { useFlightSearchStore } from "../../../store/flightSearch/flightSearchStore";
import { pluralizeText } from "../../../utils/helpers";
import FlightSearchMobile from "./FlightSearchMobile";
import { Button, Form } from "antd";
import { FlightSearchStore } from "../../../store/flightSearch/types";
import { generateMultiCityFlightRoutes } from "../../FlightSearch/helper";

import { Tuning2 } from '@solar-icons/react';
import { GREY_12 } from '../../../utils/colors';
import { formatDisplayDate, formatReturnDisplayDate } from '../../../screens/SearchListings/helpers';



type Props = {
  navigateHandler: () => void
  modify: boolean,
  refetchSearch: (arg: FlightSearchStore) => void,
  onModifyChange: (value: boolean) => void,
  onOpenFilters?: () => void
}
function FlightMobileHeader({ navigateHandler, modify, onModifyChange, refetchSearch, onOpenFilters }: Props) {
  const [form] = Form.useForm();
  const drawerContainerRef = useRef(null);
  const { isLoggedIn, setAuthModal } = useAuthStore();
  const {
    formData, updateFormData,
  } = useFlightSearchStore();
  const {
    FlightClass,
    DepartureDate,
    ReturnDate,
    numberOfAdults,
    DestinationLocationCode,
    OriginLocationCode,
    numberOfInfants,
    numberOfChildren,
    tripType,
    multiCity
  } = formData
  const isRoundTrip = tripType === 'round_trip';
  const isMultiCity = tripType === 'multi_city' && multiCity?.length > 0;

  const departure = isMultiCity ? multiCity[0].DepartureDate : DepartureDate;
  const arrival = isMultiCity ? multiCity[multiCity.length - 1].DepartureDate : ReturnDate;
  const originCode = isMultiCity ? multiCity[0].OriginLocationCode : OriginLocationCode;
  const destinationCode = isMultiCity ? multiCity[multiCity.length - 1].DestinationLocationCode : DestinationLocationCode;
  const [payload, setPayload] = useState<FlightSearchStore>(formData);

  const departureInput = (payload?.DepartureDate as any)?.DepartureDate ?? payload?.DepartureDate;
  const hasReturnDate =
    Boolean((departureInput as any)?.to && typeof (departureInput as any).to !== "function") ||
    Boolean((departureInput as any)?._i?.to);
  const tripTypeFromDate = hasReturnDate ? 'round_trip' : 'one_way';

  const numberOfPassengers = useMemo(() => {
    return numberOfAdults + (numberOfChildren || 0) + (numberOfInfants || 0);
  }, [numberOfAdults, numberOfChildren, numberOfInfants]);

  const handleSubmit = () => {
    form.validateFields().then(() => {
      const cities = form.getFieldValue('trips');
      const payloadData = cities ? { ...payload, multiCity: generateMultiCityFlightRoutes(cities), isDomestic: false } : {...payload, tripType: tripTypeFromDate};
      refetchSearch(payloadData);
      updateFormData(payloadData);
      onModifyChange(false);
    }).catch(() => { });
  }

  const handleChange = (object) => {
    setPayload({ ...payload, ...object });
    form.setFieldsValue(object);
  }

  const DrawerFooter = (
    <Button type="primary" block onClick={() => handleSubmit()}>
      Search
    </Button>
  )

  return (
    <>
      <div ref={drawerContainerRef} style={{ position: 'sticky', top: 0, zIndex: 99, background: '#fff', padding: '16px 20px 10px 20px', borderBottom: '1px solid #E5E7EB' }}>

        <Wrapper style={{ paddingTop: '10px', paddingLeft: 0, paddingRight: 0, justifyContent: 'space-between', width: '100%' }}>
          <BackIconWrapper onClick={navigateHandler}>
            <ArrowLeftIcon width={20} height={20} />
          </BackIconWrapper>

          <SearchSummaryPill onClick={() => onModifyChange(true)}>
            <div>
              <DetailsWrapper style={{ columnGap: '5px' }}>
                <StyledTitle level={5} style={{ margin: 0, fontWeight: 600 }}>
                  {originCode}
                </StyledTitle>

                {
                  isRoundTrip ? <DirectionIcon width={12} height={12} /> : <RightArrowIcon width={12} height={12} />
                }

                <StyledTitle level={5} style={{ margin: 0, fontWeight: 600 }}>
                  {destinationCode}
                </StyledTitle>

              {
                isMultiCity && (
                  <StyledParagraph size="default" color="primary">
                    - Multi City
                  </StyledParagraph>
                )
              }
              </DetailsWrapper>

              <StyledParagraph  color={GREY_12}>
                <span>
                  {
                    isRoundTrip || isMultiCity
                      ? `${formatDisplayDate(departure)} - ${
                          isMultiCity ? formatDisplayDate(arrival) : formatReturnDisplayDate(departure)
                        }`
                      : formatDisplayDate(departure)
                  }
                </span>
                <span> &nbsp;|&nbsp; {pluralizeText(numberOfPassengers, "Passenger", "Passengers")} </span>
                <span> &nbsp;|&nbsp; {FlightClass}</span>
              </StyledParagraph>
            </div>

            <EditIconWrapper onClick={(e) => {
              e.stopPropagation();
              onModifyChange(true);
            }}>
              <Pen2 weight="Outline" size={20}    />
            </EditIconWrapper>
          </SearchSummaryPill>

          <FilterIconButton onClick={onOpenFilters}>

            <Tuning2 weight="Bold" size={20}   color={GREY_12} />
          </FilterIconButton>
        </Wrapper>
      </div>

      <Drawer
        open={modify}
        onClose={() => onModifyChange(false)}
        footer={DrawerFooter}
        title="Modify Search"
        placement="bottom"
        height="100%"
        getContainer={drawerContainerRef.current as HTMLElement}
      >
        <MobileDrawerContent>
          <FlightSearchMobile onSubmit={handleSubmit} onChange={handleChange} payload={payload} form={form} />
        </MobileDrawerContent>
      </Drawer>
    </>
  );
}

FlightMobileHeader.defaultProps = {
  name: undefined,
  navigateHandler: undefined,
};

export default FlightMobileHeader;
